import { formatPersonName } from "./personName";
import { Dialog, DialogTitle, DialogContent, DialogActions, Alert, Autocomplete, TextField,
  FormControl, Button, Grid, OutlinedInput } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import DatePicker from "./DialogDatePicker";
import { normalizeDialogDate } from "./dialogDate";
import LoadingIndicator from './LoadingIndicator';
import { useState, useEffect } from "react";
import dayjs from "dayjs";
import Close from "@mui/icons-material/Close";
import Save from "@mui/icons-material/Save";

function StreckeDialog({
  open,
  onClose,
  onSave,
  wildtier,
  betriebsjahrId,
  yearLabel = "Betriebsjahr",
  betriebsjahre = [],
  jaegerListe = [],
  einrichtungen = [],
  einrichtungenLoading = false,
  wildDaten = [],
  wildLoading = false
}) {
  const [selectedYear, setSelectedYear] = useState(wildtier?.Betriebsjahr_Id ?? betriebsjahrId ?? "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [datum, setDatum] = useState(wildtier ? dayjs(wildtier.datum) : dayjs());
  const [ort, setOrt] = useState(wildtier ? wildtier.ort : "");
  const [einrichtungId, setEinrichtungId] = useState(wildtier?.EinrichtungID ?? "");
  const [bemerkung, setBemerkung] = useState(wildtier ? wildtier.bemerkung : "");
  const [wildname, setWildname] = useState("");
  const [altersklasse, setAltersklasse] = useState("");
  const [wildId, setWildId] = useState("");
  const [jaegerId, setJaegerId] = useState(wildtier && wildtier.jaeger_id != null ? String(wildtier.jaeger_id) : "");
  const [alter, setAlter] = useState(wildtier ? wildtier.alter : "");
  const [gewicht, setGewicht] = useState(wildtier ? wildtier.gewicht : "");
  const [anzahl, setAnzahl] = useState(1);
  const selectedWild = wildDaten.find(w => String(w.Id) === String(wildId));
  const historicalSelection = wildtier?.WildId != null && String(wildtier.WildId) === String(wildId);
  const unit = selectedWild?.Std_Einheit ?? (historicalSelection ? wildtier.Std_Einheit : undefined);
  const invalidAnzahl = unit === "Stk" && anzahl !== "" &&
    (!Number.isSafeInteger(Number(anzahl)) || Number(anzahl) < 0);
  const [geschlecht, setGeschlecht] = useState(wildtier ? wildtier.geschlecht : "");
  const [zerleger, setZerleger] = useState(wildtier ? wildtier.zerleger : "");
  const [zerlegedatum, setZerlegedatum] = useState(wildtier ? wildtier.zerlegedatum : "");
  const [zerlegekosten, setZerlegekosten] = useState(wildtier ? wildtier.zerlegekosten : "");

  // Dialog-Reset bei Öffnen
  useEffect(() => {
    if (open) {
      setError("");
      if (wildtier) {
        setSelectedYear(wildtier.Betriebsjahr_Id ?? betriebsjahrId ?? "");
        setDatum(wildtier.Datum ? dayjs(wildtier.Datum) : dayjs());
        setOrt(wildtier.Ort ?? "");
        setEinrichtungId(wildtier.EinrichtungID ?? "");
        setBemerkung(wildtier.Bemerkung ?? "");
        setAlter(wildtier.Wildalter ?? "");
        setGewicht(wildtier.Gewicht ?? "");
        setAnzahl(wildtier.Anzahl ?? 1);
        setGeschlecht(wildtier.Geschlecht ?? "");
        setZerleger(wildtier.Zerleger ?? "");
        setZerlegedatum(wildtier.Zerlegedatum ?? "");
        setZerlegekosten(wildtier.Zerlegekosten ?? "");

        // Jäger aus der Jägerliste zusammenbauen
        setJaegerId(wildtier.JaegerId != null ? String(wildtier.JaegerId) : "");

        // WildId, Wildname und Altersklasse direkt aus wildtier übernehmen, falls vorhanden
        setWildId(wildtier.WildId ?? "");
        setWildname(wildtier.Wildname ?? wildtier.wildname ?? "");
        setAltersklasse(wildtier.Altersklasse ?? "");
      } else {
        setSelectedYear(betriebsjahrId ?? "");
        setDatum(dayjs());
        setOrt("");
        setEinrichtungId("");
        setBemerkung("");
        setWildId("");
        setJaegerId("");
        setAlter("");
        setGewicht("");
        setAnzahl(1);
        setGeschlecht("");
        setWildname("");
        setAltersklasse("");
        setZerleger("");
        setZerlegedatum("");
        setZerlegekosten("");
      }
    }
  }, [open, wildtier, betriebsjahrId]);

  useEffect(() => {
    if (!wildId) return;
    const item = wildDaten.find(w => String(w.Id) === String(wildId));
    if (item) {
      // Resolve labels by the stored ID; never infer a replacement historical ID.
      setWildname(item.Name);
      setAltersklasse(item.Altersklasse);
    } else if (!historicalSelection) {
      setWildId("");
      setWildname("");
      setAltersklasse("");
    }
  }, [wildDaten, wildId, historicalSelection]);

  // Wildname-Auswahl: Altersklasse und WildId zurücksetzen
  const handleWildnameChange = (value) => {
    setGewicht("");
    setAnzahl(1);
    setWildname(value || "");
    setAltersklasse("");
    setWildId("");
  };

  // Altersklasse-Auswahl: WildId setzen
  const handleAltersklasseChange = (selectedAltersklasse) => {
    selectedAltersklasse = selectedAltersklasse || "";
    setAltersklasse(selectedAltersklasse);
    // Finde die Wild-Id anhand von Name und Altersklasse
    const wild = wildDaten.find(
      (w) => w.Name === wildname && w.Altersklasse === selectedAltersklasse
    );
    setWildId(wild ? wild.Id : "");
    if (wild?.Std_Einheit !== unit) {
      setGewicht("");
      setAnzahl(1);
    }
  };

  const handleSave = async () => {
    if (wildLoading || !selectedWild || invalidAnzahl || !["kg", "Stk"].includes(unit)) return;
    if (!selectedYear) return;
    setSaving(true);
    setError("");
    try {
    await onSave({
      betriebsjahr_id: selectedYear,
      datum: datum ? normalizeDialogDate(dayjs(datum)).format("YYYY-MM-DD") : "",
      jaeger_id: jaegerId,
      wild_id: wildId,
      alter: alter !== "" ? Number(alter) : null,
      gewicht: unit === "kg" && gewicht !== "" ? Number(gewicht) : null,
      anzahl: unit === "Stk" && String(anzahl).trim() !== "" ? Number(anzahl) : 1,
      geschlecht,
      ort,
      einrichtung_id: einrichtungId || null,
      bemerkung,
      zerleger,
      zerlegedatum: zerlegedatum ? normalizeDialogDate(dayjs(zerlegedatum)).format("YYYY-MM-DD") : null,
      zerlegekosten: zerlegekosten !== "" ? Number(zerlegekosten) : null
    });
    onClose();
    } catch (saveError) {
      setError(saveError.response?.data?.error || "Strecke konnte nicht gespeichert werden.");
    } finally { setSaving(false); }
  };

  // Wildnamen ohne Duplikate
  const historicalOption = historicalSelection && !selectedWild;
  const wildnamen = [...new Set([...wildDaten.map((w) => w.Name), ...(historicalOption && wildname ? [wildname] : [])])];
  // Altersklassen für den gewählten Wildnamen
  const altersklassen = [...new Set([...wildDaten
    .filter((w) => w.Name === wildname)
    .map((w) => w.Altersklasse), ...(historicalOption && altersklasse ? [altersklasse] : [])])];
  const storedEinrichtung = wildtier?.EinrichtungID != null && wildtier?.EinrichtungName
    ? { Id: wildtier.EinrichtungID, Name: wildtier.EinrichtungName } : null;
  const einrichtungOptions = storedEinrichtung && !einrichtungen.some(item => String(item.Id) === String(storedEinrichtung.Id))
    ? [storedEinrichtung, ...einrichtungen] : einrichtungen;
  const selectedEinrichtung = einrichtungOptions.find(item => String(item.Id) === String(einrichtungId)) || null;
  const selectedYearOption = betriebsjahre.find(item => String(item.Id) === String(selectedYear)) || null;
  const storedJaeger = wildtier?.JaegerId != null
    ? { Id: wildtier.JaegerId, NameNV: wildtier.NameNV || `Jäger ${wildtier.JaegerId}` } : null;
  const selectedJaeger = jaegerListe.find(item => String(item.Id) === String(jaegerId))
    || (storedJaeger && String(storedJaeger.Id) === String(jaegerId) ? storedJaeger : null);
  const jaegerLabel = option => option?.NameNV || formatPersonName(option?.Name, option?.Vorname);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Wildtier erfassen</DialogTitle>
      <DialogContent>
        {wildLoading && <LoadingIndicator label="Wilddaten werden geladen …" />}
        {error && <Alert severity="error">{error}</Alert>}
        {historicalOption && !wildLoading && <Alert severity="warning">Die gespeicherte Wildart ist im aktuellen Mandantenkatalog nicht verfügbar. Die Zuordnung bleibt erhalten; Speichern ist gesperrt.</Alert>}
        <Grid container spacing={2}>
          {/* Jagdjahr */}
          <Grid item xs={6}>
            <Autocomplete
              sx={{ width: 260 }}
              options={betriebsjahre}
              value={selectedYearOption}
              freeSolo={false}
              getOptionLabel={option => option?.Name || ""}
              isOptionEqualToValue={(option, value) => String(option.Id) === String(value.Id)}
              onChange={(_event, value) => setSelectedYear(value?.Id ?? "")}
              renderInput={params => <TextField {...params} size="small" label={yearLabel} margin="dense" />}
            />
          </Grid>
          {/* Wildname */}
          <Grid item xs={6}>
            <Autocomplete
              sx={{ width: 260 }}
              options={wildnamen}
              value={wildname || null}
              freeSolo={false}
              disabled={wildLoading}
              onChange={(_event, value) => handleWildnameChange(value)}
              renderInput={params => <TextField {...params} size="small" label="Wildart" margin="dense" />}
            />
          </Grid>
          {/* Altersklasse */}
          <Grid item xs={6}>
            <Autocomplete
              sx={{ width: 260 }}
              options={altersklassen}
              value={altersklasse || null}
              freeSolo={false}
              disabled={wildLoading}
              onChange={(_event, value) => handleAltersklasseChange(value)}
              renderInput={params => <TextField {...params} size="small" label="Altersklasse" margin="dense" />}
            />
          </Grid>
          {/* Jäger */}
          <Grid item xs={6}>
            <Autocomplete
              sx={{ width: 260 }}
              options={jaegerListe}
              value={selectedJaeger}
              freeSolo={false}
              getOptionLabel={jaegerLabel}
              isOptionEqualToValue={(option, value) => String(option.Id) === String(value.Id)}
              onChange={(_event, value) => setJaegerId(value?.Id != null ? String(value.Id) : "")}
              renderInput={params => <TextField {...params} size="small" label="Jäger" margin="dense" />}
            />
          </Grid>
          {/* Erlegungsdatum */}
          <Grid item xs={6}>
            <DatePicker
              label="Erlegungsdatum"
              value={datum}
              slotProps={{ 
                textField: { 
                  size: 'small',
                  margin: 'dense',
                  width: '260'
                } 
              }}
              onChange={(newValue) => setDatum(newValue)}
              format="DD.MM.YYYY"
            />
          </Grid>
          {/* Alter */}
          <Grid item xs={6}>
            <TextField
              sx={{ width: { xs: "100%", sm: 100 } }}
              size="small"
              label="Alter"
              margin="dense"
              value={alter}
              onChange={(e) => setAlter(e.target.value)}
              type="number"
            />
          </Grid>
          {/* Gewicht */}
          <Grid item xs={6}>
            {unit === "kg" && (
            <FormControl sx={{ width: { xs: "100%", sm: 100 } }} size="small" margin="dense">
              <OutlinedInput

                id="outlined-adornment-weight"
                endAdornment={<InputAdornment position="end">kg</InputAdornment>}
                aria-describedby="outlined-weight-helper-text"
                inputProps={{
                  'aria-label': 'weight',
                }}
                value={gewicht}
                onChange={(e) => setGewicht(e.target.value)}
                type="number"
              />
            </FormControl>
            )}
            {unit === "Stk" && (
              <TextField
                sx={{ width: { xs: "100%", sm: 100 } }}
                size="small" margin="dense" label="Anzahl" type="number"
                value={anzahl} onChange={e => setAnzahl(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                error={invalidAnzahl}
                helperText={invalidAnzahl ? "Ganze Zahl ab 0" : ""}
              />
            )}
          </Grid>
          {/* Geschlecht */}
          <Grid item xs={6}>
            <Autocomplete
              sx={{ width: 100 }}
              options={["M", "W"]}
              value={geschlecht || null}
              freeSolo={false}
              onChange={(_event, value) => setGeschlecht(value || "")}
              renderInput={params => <TextField {...params} size="small" label="Geschlecht" margin="dense" />}
            />
          </Grid>
          {/* Ort */}
          <Grid item xs={6}>
            <TextField
              sx={{ width: 180 }}
              size="small"
              label="Ort"
              margin="dense"
              value={ort}
              onChange={(e) => setOrt(e.target.value)}
            />
          </Grid>
          {/* Optionale Einrichtung; Ort bleibt während der Übergangsphase erhalten. */}
          <Grid item xs={6}>
            <Autocomplete
              sx={{ width: 340 }}
              options={einrichtungOptions}
              value={selectedEinrichtung}
              freeSolo={false}
              disabled={einrichtungenLoading}
              getOptionLabel={(option) => option?.Name || ""}
              isOptionEqualToValue={(option, value) => String(option.Id) === String(value.Id)}
              onChange={(_event, value) => setEinrichtungId(value?.Id ?? "")}
              renderInput={(params) => <TextField {...params} size="small" label="Einrichtung" margin="dense" />}
            />
          </Grid>
          {/* Bemerkung */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Bemerkung"
              margin="dense"
              value={bemerkung}
              onChange={(e) => setBemerkung(e.target.value)}
            />
          </Grid>
          {/* Zerleger */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Zerleger"
              margin="dense"
              value={zerleger}
              onChange={(e) => setZerleger(e.target.value)}
            />
          </Grid>
          {/* Zerlegedatum */}
          <Grid item xs={6}>
            <DatePicker
              fullWidth
              slotProps={{ 
                textField: { 
                  size: 'small',
                  margin: 'dense',
                  width: '240'
                } 
              }}
              label="Zerlegedatum"
              value={zerlegedatum ? dayjs(zerlegedatum) : null}
              format="DD.MM.YYYY"
              onChange={setZerlegedatum}
            />
          </Grid>
          {/* Zerlegekosten */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Zerlegekosten (€)"
              margin="dense"
              value={zerlegekosten}
              onChange={(e) => setZerlegekosten(e.target.value)}
              type="number"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" startIcon={<Close />} disabled={saving}>Abbrechen</Button>
        <Button onClick={handleSave} color="primary" variant="contained" startIcon={<Save />} disabled={saving || wildLoading || !selectedWild || !selectedYear || invalidAnzahl || !["kg", "Stk"].includes(unit) || selectedYear === "" || selectedYear == null || wildId === "" || wildId == null || jaegerId === "" || jaegerId == null}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default StreckeDialog;
